/**
 * Edit CTABlock block.
 * @module components/Blocks/CTABlock/Block
 *
 * Customizzations:
 * - Added two new fields type "color widget" one to set the background opacity and the other to set the font color. Only visible if an image was selected
 *
 */

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Segment, Accordion } from 'semantic-ui-react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import {
  ObjectBrowserWidget,
  CheckboxWidget,
  TextWidget,
} from '@plone/volto/components';
import { LinkToWidget } from 'design-comuni-plone-theme/components/ItaliaTheme';

import { ColorListWidget } from 'design-comuni-plone-theme/components/ItaliaTheme';

const messages = defineMessages({
  ctaImage: {
    id: 'ctaImage',
    defaultMessage: "Seleziona l'immagine da mostrare",
  },
  ctaImageEnable: {
    id: 'ctaImageEnable',
    defaultMessage: "Mostra l'immagine",
  },
  ctaFullWidth: {
    id: 'ctaFullWidth',
    defaultMessage: 'Mostra a pieno schermo',
  },
  ctaLinkTitle: {
    id: 'ctaLinkTitle',
    defaultMessage: 'Titolo per il link della CTA',
  },
  ctaLink: {
    id: 'ctaLink',
    defaultMessage: 'Link della CTA',
  },
  opacity_title: {
    id: 'opacity_title',
    defaultMessage: 'Background image opacity',
  },
  default: {
    id: 'default ',
    defaultMessage: 'Full opacity',
  },
  opacity: {
    id: 'opacity',
    defaultMessage: 'Reduced opacity',
  },
  no_opacity: {
    id: 'no_opacity',
    defaultMessage: 'Without opacity',
  },
  font_title: {
    id: 'font_title',
    defaultMessage: 'Font color',
  },
  dark_font: {
    id: 'dark_font ',
    defaultMessage: 'Font dark',
  },
  light_font: {
    id: 'light_font',
    defaultMessage: 'Font light',
  },
});

const Sidebar = ({ data, block, onChangeBlock, openObjectBrowser }) => {
  const intl = useIntl();
  const hasImage = data?.showImage && data?.ctaImage?.length > 0;

  useEffect(() => {
    if (data.showFullWidth === undefined) {
      onChangeBlock(block, {
        ...data,
        showFullWidth: true,
      });
    }
  }, []);

  if (data.showImage === null || data.showImage === undefined) {
    data.showImage = false;
  }

  const bg_opacity = [
    {
      name: 'default',
      label: intl.formatMessage(messages.default),
    },
    {
      name: 'opacity',
      label: intl.formatMessage(messages.opacity),
    },
    {
      name: 'no_opacity',
      label: intl.formatMessage(messages.no_opacity),
    },
  ];

  const font_color = [
    {
      name: 'dark_font',
      label: intl.formatMessage(messages.dark_font),
    },
    {
      name: 'light_font',
      label: intl.formatMessage(messages.light_font),
    },
  ];

  return (
    <Segment.Group raised key={block.id || block}>
      <header className="header pulled">
        <h2>
          <FormattedMessage id="cta_block" defaultMessage="Blocco CTA" />
        </h2>
      </header>
      <Accordion className="form">
        <Accordion.Content active={true}>
          <ObjectBrowserWidget
            id="ctaImage"
            title={intl.formatMessage(messages.ctaImage)}
            value={data.ctaImage || []}
            mode="image"
            widgetOptions={{
              pattern_options: { selectableTypes: ['Image'] },
            }}
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                [name]: value || [],
                showImage: !!value,
              });
            }}
          />
          <CheckboxWidget
            id="showImage"
            title={intl.formatMessage(messages.ctaImageEnable)}
            value={data.ctaImage?.length > 0 && data.showImage}
            onChange={(name, checked) => {
              onChangeBlock(block, { ...data, [name]: checked });
            }}
            isDisabled={!data.ctaImage?.length > 0}
          />
          <CheckboxWidget
            id="showFullWidth"
            title={intl.formatMessage(messages.ctaFullWidth)}
            value={data.showFullWidth ? data.showFullWidth : false}
            onChange={(name, checked) => {
              onChangeBlock(block, { ...data, [name]: checked });
            }}
          />

          <TextWidget
            id="ctaLinkTitle"
            title={intl.formatMessage(messages.ctaLinkTitle)}
            value={data.ctaLinkTitle}
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                [name]: value,
              });
            }}
          />
          <LinkToWidget
            data={data}
            openObjectBrowser={openObjectBrowser}
            linkField="ctaLink"
            title={intl.formatMessage(messages.ctaLink)}
            showTarget={true}
            onChange={(name, value) =>
              onChangeBlock(block, {
                ...data,
                [name]: value,
              })
            }
          />
          {hasImage && (
            <ColorListWidget
              id="opacity"
              title={intl.formatMessage(messages.opacity_title)}
              value={data.opacity}
              onChange={(id, value) => {
                onChangeBlock(block, {
                  ...data,
                  [id]: value,
                });
              }}
              colors={bg_opacity}
            />
          )}
          {hasImage && (
            <ColorListWidget
              id="font_color"
              title={intl.formatMessage(messages.font_title)}
              value={data.font_color}
              onChange={(id, value) => {
                onChangeBlock(block, {
                  ...data,
                  [id]: value,
                });
              }}
              colors={font_color}
            />
          )}
        </Accordion.Content>
      </Accordion>
    </Segment.Group>
  );
};

Sidebar.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  block: PropTypes.string,
  onChangeBlock: PropTypes.func,
  selected: PropTypes.any,
  setSelected: PropTypes.func,
};

export default injectIntl(Sidebar);
